import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import axios from 'axios';
import CONFIG from '../../config';
import { motion, AnimatePresence } from 'framer-motion';
import Swal from 'sweetalert2';
import LoadingSpinner from '../common/LoadingSpinner';

const UserManagement = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingUser, setEditingUser] = useState(null);

    const [formData, setFormData] = useState({
        full_name: '',
        email: '',
        phone: '',
        role: 'STAGE_EVALUATOR',
        designation: '',
        description: '',
        is_active: true
    });

    const [filterName, setFilterName] = useState('');
    const [filterRole, setFilterRole] = useState('all');

    const fetchUsers = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const resp = await axios.get(`${CONFIG.API_URL}/api/users`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setUsers(resp.data);
        } catch (err) {
            console.error('Failed to fetch users:', err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setShowModal(false);
        const currentUser = editingUser;
        setEditingUser(null);

        Swal.fire({
            title: 'Processing Request...',
            text: currentUser ? 'Synchronizing staff profile updates...' : 'Authenticating new mission credentials...',
            allowOutsideClick: false,
            showConfirmButton: false,
            didOpen: () => {
                Swal.showLoading();
            },
            background: 'var(--white)',
            color: 'var(--text-dark)',
            iconColor: 'var(--primary)'
        });

        try {
            const token = localStorage.getItem('token');
            const config = { headers: { Authorization: `Bearer ${token}` } };

            if (currentUser) {
                await axios.put(`${CONFIG.API_URL}/api/users/${currentUser._id}`, formData, config);
                Swal.fire({
                    icon: 'success',
                    title: 'Mission Updated',
                    text: 'Staff profile has been synchronized successfully.',
                    timer: 2500,
                    showConfirmButton: false,
                    iconColor: 'var(--primary)',
                    background: 'var(--white)'
                });
            } else {
                await axios.post(`${CONFIG.API_URL}/api/users`, formData, config);
                Swal.fire({
                    icon: 'success',
                    title: 'Deployment Successful',
                    text: 'New staff member has been registered and notified.',
                    timer: 2500,
                    showConfirmButton: false,
                    iconColor: '#10b981',
                    background: 'var(--white)'
                });
            }
            fetchUsers();
        } catch (err) {
            Swal.fire({
                icon: 'error',
                title: 'Operation Suspended',
                text: err.response?.data?.message || 'Failed to complete mission registration',
                confirmButtonColor: 'var(--primary)',
                background: 'var(--white)'
            });
        }
    };

    const handleDelete = async (id) => {
        const result = await Swal.fire({
            title: 'Are you sure?',
            text: "You won't be able to revert this!",
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: 'var(--primary)',
            cancelButtonColor: 'var(--text-muted)',
            confirmButtonText: 'Yes, delete it!',
            background: 'var(--white)',
            color: 'var(--text-dark)'
        });

        if (result.isConfirmed) {
            Swal.fire({
                title: 'Retiring Staff...',
                text: 'Removing access permissions...',
                allowOutsideClick: false,
                showConfirmButton: false,
                didOpen: () => {
                    Swal.showLoading();
                }
            });

            try {
                const token = localStorage.getItem('token');
                await axios.delete(`${CONFIG.API_URL}/api/users/${id}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                Swal.fire({
                    icon: 'success',
                    title: 'Staff Retired',
                    text: 'The user account has been successfully removed.',
                    timer: 2000,
                    showConfirmButton: false,
                    iconColor: 'var(--primary)'
                });
                fetchUsers();
            } catch (err) {
                Swal.fire({
                    icon: 'error',
                    title: 'Recall Failed',
                    text: 'Security override prevented deletion.',
                    confirmButtonColor: 'var(--primary)'
                });
            }
        }
    };

    return (
        <>
            <div className="main-view animate-fade-in">
                <div className="glass-card">
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '32px' }}>
                        <div>
                            <h1 style={{ fontSize: '28px', fontWeight: 800, color: 'var(--text-dark)' }}>Staff Management</h1>
                            <p style={{ color: 'var(--text-muted)' }}>Create and manage field personnel roles.</p>
                        </div>
                        <div style={{ display: 'flex', gap: '12px' }}>
                            <div style={{ position: 'relative' }}>
                                <Icon icon="ion:search-outline" style={{ position: 'absolute', left: '12px', top: '12px', color: 'var(--text-muted)' }} />
                                <input
                                    type="text"
                                    placeholder="Search staff Name..."
                                    className="input-field"
                                    style={{ width: '240px', paddingLeft: '40px' }}
                                    value={filterName}
                                    onChange={(e) => setFilterName(e.target.value)}
                                />
                            </div>
                            <select
                                className="input-field"
                                style={{ width: '180px' }}
                                value={filterRole}
                                onChange={(e) => setFilterRole(e.target.value)}
                            >
                                <option value="all">Every Role</option>
                                <option value="ADMIN">Master Admin</option>
                                <option value="STAGE_EVALUATOR">Stage Evaluator</option>
                                <option value="RUN_OFFICER">Run Officer</option>
                                <option value="DOCTOR">Doctor</option>
                                <option value="FIELD_MONITOR">Field Monitor</option>
                                <option value="ENTRY_STAFF">Entry Staff</option>
                            </select>
                            <button
                                className="btn-primary"
                                onClick={() => {
                                    setEditingUser(null);
                                    setFormData({ full_name: '', email: '', phone: '', role: 'STAGE_EVALUATOR', designation: '', description: '', is_active: true });
                                    setShowModal(true);
                                }}
                                style={{ width: 'auto', padding: '12px 24px' }}
                            >
                                <Icon icon="ion:person-add-outline" style={{ marginRight: '8px' }} />
                                Add New Staff
                            </button>
                        </div>
                    </div>

                    <div className="table-responsive">
                        <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0 12px' }}>
                            <thead>
                                <tr style={{ textAlign: 'center', color: 'var(--text-muted)', fontSize: '12px', fontWeight: 800, letterSpacing: '1px' }}>
                                    <th style={{ textAlign: 'left', padding: '0 24px' }}>STAFF MEMBER</th>
                                    <th>ROLE</th>
                                    <th>DESIGNATION</th>
                                    <th>STATUS</th>
                                    <th style={{ textAlign: 'right', padding: '0 24px' }}>ACTIONS</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr>
                                        <td colSpan="5" style={{ padding: '140px 0', textAlign: 'center' }}>
                                            <LoadingSpinner message="Fetching Staff Mission Data..." />
                                        </td>
                                    </tr>
                                ) : (() => {
                                    const filteredUsers = users.filter(u =>
                                        u.full_name?.toLowerCase().includes(filterName.toLowerCase()) &&
                                        (filterRole === 'all' || u.role === filterRole)
                                    );

                                    if (filteredUsers.length === 0) {
                                        return (
                                            <tr>
                                                <td colSpan="5" style={{ padding: '80px 0', textAlign: 'center' }}>
                                                    <motion.div
                                                        initial={{ opacity: 0, y: 20 }}
                                                        animate={{ opacity: 1, y: 0 }}
                                                        style={{
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            alignItems: 'center',
                                                            gap: '20px'
                                                        }}
                                                    >
                                                        <div style={{
                                                            width: '80px',
                                                            height: '80px',
                                                            borderRadius: '24px',
                                                            background: 'rgba(128, 0, 0, 0.05)',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            border: '1px solid rgba(128, 0, 0, 0.1)'
                                                        }}>
                                                            <Icon icon="solar:users-group-two-rounded-bold-duotone" width="40" color="var(--primary)" style={{ opacity: 0.6 }} />
                                                        </div>
                                                        <div>
                                                            <h3 style={{ fontSize: '20px', fontWeight: 800, color: 'var(--text-dark)', marginBottom: '8px' }}>No Mission Data Available</h3>
                                                            <p style={{ fontSize: '14px', color: 'var(--text-muted)', maxWidth: '320px', margin: '0 auto', lineHeight: '1.6' }}>
                                                                We couldn't find any staff members matching <span style={{ color: 'var(--primary)', fontWeight: 700 }}>"{filterName || filterRole}"</span>. Try adjusting your mission parameters.
                                                            </p>
                                                        </div>
                                                        <button
                                                            onClick={() => { setFilterName(''); setFilterRole('all'); }}
                                                            style={{
                                                                background: 'none',
                                                                border: 'none',
                                                                color: 'var(--primary)',
                                                                fontWeight: 800,
                                                                fontSize: '14px',
                                                                cursor: 'pointer',
                                                                textDecoration: 'underline',
                                                                textUnderlineOffset: '4px',
                                                                transition: 'all 0.2s'
                                                            }}
                                                            onMouseOver={(e) => e.target.style.opacity = 0.8}
                                                            onMouseOut={(e) => e.target.style.opacity = 1}
                                                        >
                                                            Clear all active filters
                                                        </button>
                                                    </motion.div>
                                                </td>
                                            </tr>
                                        );
                                    }

                                    return filteredUsers.map(u => (
                                        <tr key={u._id} className="table-row-hover" style={{ background: 'rgba(255, 255, 255, 0.6)', borderRadius: '16px', boxShadow: '0 2px 10px rgba(0,0,0,0.02)' }}>
                                            <td style={{ padding: '20px 24px', borderRadius: '16px 0 0 16px' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                                                    <div style={{
                                                        width: '44px',
                                                        height: '44px',
                                                        borderRadius: '14px',
                                                        background: u.role === 'ADMIN' ? 'var(--primary)' : '#f1f5f9',
                                                        color: u.role === 'ADMIN' ? 'white' : 'var(--primary)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        fontWeight: 800,
                                                        fontSize: '18px'
                                                    }}>{u.full_name?.charAt(0)}</div>
                                                    <div style={{ textAlign: 'left' }}>
                                                        <div style={{ fontWeight: 800, color: 'var(--text-dark)', fontSize: '15px' }}>{u.full_name} {u.role === 'ADMIN' && <Icon icon="ion:checkmark-circle" style={{ color: '#3b82f6', fontSize: '14px', verticalAlign: 'middle' }} />}</div>
                                                        <div style={{ fontSize: '12px', color: 'var(--text-muted)', fontWeight: 500 }}>{u.email}</div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td style={{ textAlign: 'center' }}>
                                                <span style={{
                                                    padding: '6px 14px',
                                                    borderRadius: '10px',
                                                    background: 'rgba(128,0,0,0.05)',
                                                    color: 'var(--primary)',
                                                    fontSize: '11px',
                                                    fontWeight: 800
                                                }}>{u.role}</span>
                                            </td>
                                            <td style={{ textAlign: 'center', fontWeight: 700, color: 'var(--text-muted)', fontSize: '14px' }}>{u.designation}</td>
                                            <td style={{ textAlign: 'center' }}>
                                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                                                    <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: u.is_active ? '#10b981' : '#f43f5e', boxShadow: `0 0 10px ${u.is_active ? '#10b981' : '#f43f5e'}` }}></div>
                                                    <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-dark)' }}>{u.is_active ? 'Active' : 'Inactive'}</span>
                                                </div>
                                            </td>
                                            <td style={{ textAlign: 'right', padding: '0 24px', borderRadius: '0 16px 16px 0' }}>
                                                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                                                    <button
                                                        onClick={() => {
                                                            setEditingUser(u);
                                                            setFormData({
                                                                full_name: u.full_name,
                                                                email: u.email,
                                                                phone: u.phone,
                                                                role: u.role,
                                                                designation: u.designation,
                                                                description: u.description || '',
                                                                is_active: u.is_active
                                                            });
                                                            setShowModal(true);
                                                        }}
                                                        style={{
                                                            background: 'white', border: '1px solid var(--border-light)',
                                                            color: 'var(--primary)', cursor: 'pointer', padding: '10px',
                                                            borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                            boxShadow: '0 2px 5px rgba(0,0,0,0.05)', transition: 'all 0.2s'
                                                        }}
                                                        onMouseOver={(e) => e.currentTarget.style.background = '#fdf2f2'}
                                                        onMouseOut={(e) => e.currentTarget.style.background = 'white'}
                                                    >
                                                        <Icon icon="ion:create-outline" width="18" />
                                                    </button>
                                                    {u.role !== 'ADMIN' && (
                                                        <button
                                                            onClick={() => handleDelete(u._id)}
                                                            style={{
                                                                background: '#fff1f2', border: '1px solid #fee2e2',
                                                                color: '#f43f5e', cursor: 'pointer', padding: '10px',
                                                                borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                                boxShadow: '0 2px 5px rgba(244,63,94,0.05)', transition: 'all 0.2s'
                                                            }}
                                                            onMouseOver={(e) => e.currentTarget.style.background = '#fecdd3'}
                                                            onMouseOut={(e) => e.currentTarget.style.background = '#fff1f2'}
                                                        >
                                                            <Icon icon="ion:trash-outline" width="18" />
                                                        </button>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ));
                                })()}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <AnimatePresence>
                {showModal && (
                    <div style={{ position: 'fixed', inset: 0, zIndex: 2000, overflow: 'hidden' }}>
                        {/* Backdrop */}
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            onClick={() => setShowModal(false)}
                            style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.3)', backdropFilter: 'blur(8px)' }}
                        />

                        {/* Drawer */}
                        <motion.div
                            initial={{ x: '100%' }}
                            animate={{ x: 0 }}
                            exit={{ x: '100%' }}
                            transition={{ type: 'spring', damping: 25, stiffness: 200 }}
                            style={{
                                position: 'fixed',
                                top: 0,
                                right: 0,
                                width: '500px',
                                height: '100%',
                                background: 'white',
                                boxShadow: '-10px 0 50px rgba(0,0,0,0.1)',
                                zIndex: 2100,
                                display: 'flex',
                                flexDirection: 'column'
                            }}
                        >
                            {/* Header - Fixed */}
                            <div style={{ padding: '32px', borderBottom: '1px solid var(--border-light)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', background: 'white' }}>
                                <h2 style={{ fontSize: '24px', fontWeight: 800, color: 'var(--text-dark)' }}>
                                    {editingUser ? 'Edit Staff Member' : 'Add New Staff Member'}
                                </h2>
                                <button onClick={() => setShowModal(false)} style={{ background: 'rgba(0,0,0,0.05)', border: 'none', borderRadius: '50%', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                                    <Icon icon="ion:close-outline" width="24" />
                                </button>
                            </div>

                            {/* Scrollable Form Body */}
                            <div style={{ flex: 1, overflowY: 'auto', padding: '32px 48px' }}>
                                <form id="user-form" onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                                    <div className="form-group">
                                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 700, fontSize: '12px', color: 'var(--text-muted)' }}>FULL NAME</label>
                                        <input
                                            className="input-field"
                                            value={formData.full_name}
                                            onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                                            required
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 700, fontSize: '12px', color: 'var(--text-muted)' }}>EMAIL ADDRESS</label>
                                        <input
                                            type="email"
                                            className="input-field"
                                            value={formData.email}
                                            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                            required
                                            disabled={!!editingUser}
                                        />
                                    </div>

                                    <div className="form-group">
                                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 700, fontSize: '12px', color: 'var(--text-muted)' }}>PHONE NUMBER</label>
                                        <input
                                            type="text"
                                            className="input-field"
                                            value={formData.phone}
                                            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                                        />
                                    </div>

                                    {!editingUser && (
                                        <div className="form-group">
                                            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 700, fontSize: '12px', color: 'var(--text-muted)' }}>PASSWORD</label>
                                            <div style={{ display: 'flex', gap: '8px' }}>
                                                <input
                                                    type="text"
                                                    className="input-field"
                                                    value={formData.password}
                                                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                                                    placeholder="Staff@123"
                                                    style={{ flex: 1 }}
                                                />
                                                <button
                                                    type="button"
                                                    className="btn-secondary"
                                                    onClick={() => {
                                                        const pass = Math.random().toString(36).slice(-8) + '@ICFF' + Math.floor(Math.random() * 100);
                                                        setFormData({ ...formData, password: pass });
                                                    }}
                                                    style={{ padding: '0 12px', width: 'auto', whiteSpace: 'nowrap', fontSize: '12px' }}
                                                >
                                                    <Icon icon="ion:refresh-outline" style={{ marginRight: '4px' }} />
                                                    Generate
                                                </button>
                                            </div>
                                            <p style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '4px' }}>
                                                Leave blank to use default (Staff@123)
                                            </p>
                                        </div>
                                    )}

                                    <div className="form-group">
                                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 700, fontSize: '12px', color: 'var(--text-muted)' }}>ROLE</label>
                                        <select
                                            className="input-field"
                                            value={formData.role}
                                            onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                                            style={{ width: '100%' }}
                                        >
                                            {editingUser && formData.role === 'ADMIN' && (
                                                <option value="ADMIN">Master Admin</option>
                                            )}
                                            <option value="STAGE_EVALUATOR">Stage Evaluator</option>
                                            <option value="RUN_OFFICER">Run Officer</option>
                                            <option value="DOCTOR">Doctor / Medical</option>
                                            <option value="FIELD_MONITOR">Field Monitor</option>
                                            <option value="ENTRY_STAFF">Entry Staff</option>
                                        </select>
                                    </div>

                                    <div className="form-group">
                                        <label style={{ display: 'block', marginBottom: '12px', fontWeight: 700, fontSize: '12px', color: 'var(--text-muted)' }}>ACCOUNT STATUS</label>
                                        <div
                                            onClick={() => {
                                                if (editingUser?.role !== 'ADMIN') {
                                                    setFormData({ ...formData, is_active: !formData.is_active });
                                                }
                                            }}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '12px',
                                                cursor: editingUser?.role === 'ADMIN' ? 'not-allowed' : 'pointer',
                                                padding: '12px',
                                                borderRadius: '12px',
                                                background: '#f8fafc',
                                                opacity: editingUser?.role === 'ADMIN' ? 0.6 : 1,
                                                border: '1px solid var(--border-light)'
                                            }}
                                        >
                                            <div style={{
                                                width: '40px',
                                                height: '20px',
                                                borderRadius: '20px',
                                                background: formData.is_active ? '#10b981' : '#cbd5e1',
                                                position: 'relative',
                                                transition: 'all 0.3s'
                                            }}>
                                                <div style={{
                                                    width: '16px',
                                                    height: '16px',
                                                    borderRadius: '50%',
                                                    background: 'white',
                                                    position: 'absolute',
                                                    top: '2px',
                                                    left: formData.is_active ? '22px' : '2px',
                                                    transition: 'all 0.3s',
                                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                                                }} />
                                            </div>
                                            <span style={{ fontSize: '14px', fontWeight: 700, color: formData.is_active ? '#059669' : 'var(--text-muted)' }}>
                                                {formData.is_active ? 'Account Active' : 'Account Deactivated'}
                                            </span>
                                            {editingUser?.role === 'ADMIN' && (
                                                <span style={{ fontSize: '10px', color: 'var(--primary)', fontWeight: 800, marginLeft: 'auto' }}>SYSTEM PROTECTED</span>
                                            )}
                                        </div>
                                        <p style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '8px' }}>
                                            {editingUser?.role === 'ADMIN'
                                                ? 'The Master Admin account cannot be deactivated for safety reasons.'
                                                : 'Deactivated accounts will be immediately barred from logging into the command center.'}
                                        </p>
                                    </div>

                                    <div className="form-group">
                                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 700, fontSize: '12px', color: 'var(--text-muted)' }}>DESCRIPTION</label>
                                        <textarea
                                            className="input-field"
                                            value={formData.description}
                                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                            rows="3"
                                            style={{ width: '100%', minHeight: '80px', paddingTop: '10px' }}
                                        />
                                    </div>
                                </form>
                            </div>

                            {/* Footer - Fixed */}
                            <div style={{ padding: '24px 48px', borderTop: '1px solid var(--border-light)', display: 'flex', gap: '16px', background: '#f8fafc' }}>
                                <button type="submit" form="user-form" className="btn-primary" style={{ flex: 1 }}>
                                    {editingUser ? 'Update User' : 'Create Staff Member'}
                                </button>
                                <button type="button" className="btn-secondary" onClick={() => setShowModal(false)} style={{ flex: 1 }}>
                                    Cancel
                                </button>
                            </div>
                        </motion.div>
                    </div>
                )}
            </AnimatePresence>
        </>
    );
};

export default UserManagement;
